import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playProfileComplete, playSuccess } from '../utils/SoundManager';
import { SparkleStars, Confetti } from '../components/VisualEffects';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ARAB_COUNTRIES = [
  'السعودية', 'مصر', 'الإمارات', 'الكويت', 'قطر', 'البحرين', 'عمان',
  'الأردن', 'لبنان', 'سوريا', 'العراق', 'اليمن', 'فلسطين', 'ليبيا',
  'السودان', 'تونس', 'الجزائر', 'المغرب', 'موريتانيا', 'جيبوتي', 'الصومال'
];

function ProfileSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showStars, setShowStars] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  const [profile, setProfile] = useState({
    gender: '',
    age: '',
    nationality: '',
    city: '',
    neighborhood: '',
    has_tribe: false,
    tribe_name: '',
    employment_status: 'موظف',
    job: '',
    job_specialization: '',
    education: '',
    monthly_income: '',
    salary_range: '',
    hair_length: '',
    hair_color: '',
    height: '',
    weight: '',
    skin_color: '',
    beauty_rating: 5,
    prayer_level: '',
    hijab_status: '',
    health_status: 'سليم',
    health_details: '',
    smoker: 'لا',
    marital_status: '',
    previous_marriages: 0,
    is_polygamous: false,
    seeking_wife_number: '',
    marriage_type: 'عادي',
    wants_children: 'نعم',
    current_children: 0,
    children_live_with_user: '',
    housing_type: '',
    owns_car: false,
    drives_car: false,
    housing_preference: '',
    hobbies: [],
    about_me: '',
    ideal_partner: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_relation: ''
  });

  // جلب بيانات الملف الشخصي المحفوظة
  useEffect(() => {
    const fetchProfile = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setInitialLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true,
          headers: { 'X-User-Id': userId }
        });

        if (response.data) {
          const userData = response.data;
          // التحقق من أن الملف مكتمل
          if (userData.gender && userData.age) {
            setIsEditMode(true);
          }
          
          // تحديث الحالة بالبيانات المحفوظة
          setProfile(prev => ({
            ...prev,
            gender: userData.gender || '',
            age: userData.age || '',
            nationality: userData.nationality || '',
            city: userData.city || '',
            neighborhood: userData.neighborhood || '',
            has_tribe: userData.has_tribe || false,
            tribe_name: userData.tribe_name || '',
            employment_status: userData.employment_status || 'موظف',
            job: userData.job || '',
            job_specialization: userData.job_specialization || '',
            education: userData.education || '',
            monthly_income: userData.monthly_income || '',
            salary_range: userData.salary_range || '',
            hair_length: userData.hair_length || '',
            hair_color: userData.hair_color || '',
            height: userData.height || '',
            weight: userData.weight || '',
            skin_color: userData.skin_color || '',
            beauty_rating: userData.beauty_rating || 5,
            prayer_level: userData.prayer_level || '',
            hijab_status: userData.hijab_status || '',
            health_status: userData.health_status || 'سليم',
            health_details: userData.health_details || '',
            marital_status: userData.marital_status || '',
            previous_marriages: userData.previous_marriages || 0,
            is_polygamous: userData.is_polygamous || false,
            seeking_wife_number: userData.seeking_wife_number || '',
            marriage_type: userData.marriage_type || 'عادي',
            wants_children: userData.wants_children || 'نعم',
            current_children: userData.current_children || 0,
            children_live_with_user: userData.children_live_with_user || '',
            housing_type: userData.housing_type || '',
            owns_car: userData.owns_car || false,
            drives_car: userData.drives_car || false,
            housing_preference: userData.housing_preference || '',
            hobbies: userData.hobbies || [],
            about_me: userData.about_me || '',
            ideal_partner: userData.ideal_partner || '',
            guardian_name: userData.guardian_name || '',
            guardian_phone: userData.guardian_phone || '',
            guardian_relation: userData.guardian_relation || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const hobbiesArray = typeof profile.hobbies === 'string' 
        ? profile.hobbies.split(',').map(h => h.trim()).filter(h => h)
        : (Array.isArray(profile.hobbies) ? profile.hobbies : []);
      
      // التحقق من الحقول المطلوبة
      if (!profile.gender || !profile.age || !profile.nationality || !profile.city) {
        setError('يرجى إكمال جميع الحقول المطلوبة');
        setLoading(false);
        return;
      }
      
      if (hobbiesArray.length === 0) {
        setError('يرجى اختيار هواية واحدة على الأقل');
        setLoading(false);
        return;
      }

      const userId = localStorage.getItem('userId');
      if (!userId) {
        setError('يرجى تسجيل الدخول أولاً');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
        
      const response = await axios.post(`${BACKEND_URL}/api/profile/complete`, 
        { ...profile, hobbies: hobbiesArray }, 
        { 
          withCredentials: true,
          headers: {
            'X-User-Id': userId
          }
        }
      );
      
      if (response.data) {
        // تشغيل صوت ومؤثرات احتفالية عند إكمال الملف الشخصي
        playProfileComplete();
        setShowStars(true);
        setShowConfetti(true);
        setTimeout(() => navigate('/dashboard'), 2500);
      }
    } catch (err) {
      console.error('Profile save error:', err);
      const errorMessage = err.response?.data?.detail || 'حدث خطأ، حاول مرة أخرى';
      setError(errorMessage);
      
      // إذا كان الخطأ بسبب عدم تسجيل الدخول
      if (err.response?.status === 401) {
        setError('يرجى تسجيل الدخول أولاً');
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step < 7) {
      playSuccess(); // صوت عند الانتقال للخطوة التالية
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  // شاشة التحميل الأولية
  if (initialLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>جاري تحميل الملف الشخصي...</p>
      </div>
    );
  }

  // Step 1: المعلومات الأساسية
  const renderStep1 = () => (
    <div>
      <h2 style={{ marginBottom: '14px', color: 'var(--primary)', fontSize: '24px' }}>
        ✨ المعلومات الأساسية
      </h2>
      
      <div className="form-group">
        <label className="form-label">الجنس *</label>
        <select 
          className="select" 
          value={profile.gender} 
          onChange={(e) => setProfile({...profile, gender: e.target.value})} 
          required
        >
          <option value="">اختر الجنس</option>
          <option value="male">ذكر 👨</option>
          <option value="female">أنثى 👩</option>
        </select>
      </div>

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">العمر *</label>
          <input 
            type="number" 
            className="input" 
            value={profile.age} 
            onChange={(e) => setProfile({...profile, age: e.target.value})} 
            placeholder="مثال: 28"
            min="18"
            max="80"
            required 
          />
        </div>
        <div className="form-group">
          <label className="form-label">الجنسية *</label>
          <select 
            className="select" 
            value={profile.nationality} 
            onChange={(e) => setProfile({...profile, nationality: e.target.value})} 
            required
          >
            <option value="">اختر الجنسية</option>
            {ARAB_COUNTRIES.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">المدينة *</label>
          <input 
            type="text" 
            className="input" 
            value={profile.city} 
            onChange={(e) => setProfile({...profile, city: e.target.value})} 
            placeholder="مثال: الرياض"
            required 
          />
        </div>
        <div className="form-group">
          <label className="form-label">الحي *</label>
          <input 
            type="text" 
            className="input" 
            value={profile.neighborhood} 
            onChange={(e) => setProfile({...profile, neighborhood: e.target.value})} 
            placeholder="مثال: العليا"
            required 
          />
        </div>
      </div>

      <div className="form-group">
        <div className="checkbox-container">
          <input 
            type="checkbox" 
            className="checkbox" 
            checked={profile.has_tribe} 
            onChange={(e) => setProfile({...profile, has_tribe: e.target.checked})} 
            id="has_tribe"
          />
          <label htmlFor="has_tribe" className="checkbox-label">
            أنتمي لقبيلة
          </label>
        </div>
        {profile.has_tribe && (
          <input 
            type="text" 
            className="input" 
            style={{marginTop: '12px'}} 
            placeholder="اسم القبيلة" 
            value={profile.tribe_name} 
            onChange={(e) => setProfile({...profile, tribe_name: e.target.value})} 
          />
        )}
      </div>
    </div>
  );

  // Step 2: الوظيفة والتعليم
  const renderStep2 = () => {
    const jobCategories = [
      'طبيب',
      'مهندس',
      'معلم/أستاذ',
      'محاسب',
      'محامي',
      'صيدلي',
      'ممرض/ممرضة',
      'موظف حكومي',
      'موظف قطاع خاص',
      'رجل أعمال/سيدة أعمال',
      'عسكري',
      'طيار',
      'مصمم/مصممة',
      'مبرمج/مطور',
      'تاجر',
      'فني/حرفي',
      'سائق',
      'عامل',
      'طالب/طالبة',
      'متقاعد/متقاعدة',
      'أخرى'
    ];

    return (
    <div>
      <h2 style={{ marginBottom: '14px', color: 'var(--primary)', fontSize: '24px' }}>
        💼 الوظيفة والتعليم
      </h2>
      
      <div className="form-group">
        <label className="form-label">الحالة الوظيفية *</label>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="radio" 
              name="employment_status"
              value="موظف"
              checked={profile.employment_status === 'موظف'}
              onChange={(e) => setProfile({...profile, employment_status: e.target.value})}
            />
            <span>موظف/موظفة</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="radio" 
              name="employment_status"
              value="غير موظف"
              checked={profile.employment_status === 'غير موظف'}
              onChange={(e) => setProfile({...profile, employment_status: e.target.value, job: '', job_specialization: ''})}
            />
            <span>غير موظف/موظفة</span>
          </label>
        </div>
      </div>

      {profile.employment_status === 'موظف' && (
        <>
          <div className="form-group">
            <label className="form-label">المسمى الوظيفي *</label>
            <select 
              className="select" 
              value={profile.job} 
              onChange={(e) => setProfile({...profile, job: e.target.value})} 
              required
            >
              <option value="">اختر المسمى الوظيفي</option>
              {jobCategories.map((job) => (
                <option key={job} value={job}>{job}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">التخصص الدقيق *</label>
            <input 
              type="text" 
              className="input" 
              value={profile.job_specialization} 
              onChange={(e) => setProfile({...profile, job_specialization: e.target.value})} 
              placeholder="مثال: طبيب أسنان، مهندس برمجيات، معلم رياضيات"
              required 
            />
            <p style={{fontSize: '13px', color: 'var(--text-light)', marginTop: '6px'}}>
              💡 التخصص الدقيق مهم للتوافق المهني
            </p>
          </div>
        </>
      )}

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">المؤهل التعليمي *</label>
          <select 
            className="select" 
            value={profile.education} 
            onChange={(e) => setProfile({...profile, education: e.target.value})} 
            required
          >
            <option value="">اختر المؤهل</option>
            <option value="ثانوي">ثانوي</option>
            <option value="دبلوم">دبلوم</option>
            <option value="بكالوريوس">بكالوريوس</option>
            <option value="ماجستير">ماجستير</option>
            <option value="دكتوراه">دكتوراه</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">مقدار الراتب (اختياري)</label>
          <select 
            className="select" 
            data-testid="salary-range-select"
            value={profile.salary_range} 
            onChange={(e) => setProfile({...profile, salary_range: e.target.value})}
          >
            <option value="">اختر مقدار الراتب</option>
            <option value="أقل من 5,000 ر.س">أقل من 5,000 ر.س</option>
            <option value="5,000 - 10,000 ر.س">5,000 - 10,000 ر.س</option>
            <option value="10,000 - 15,000 ر.س">10,000 - 15,000 ر.س</option>
            <option value="15,000 - 20,000 ر.س">15,000 - 20,000 ر.س</option>
            <option value="أكثر من 20,000 ر.س">أكثر من 20,000 ر.س</option>
            <option value="لاحقاً">لاحقاً</option>
          </select>
        </div>
      </div>
    </div>
  );
  }

  // Step 3: المظهر الخارجي
  const renderStep3 = () => (
    <div>
      <h2 style={{ marginBottom: '14px', color: 'var(--primary)', fontSize: '24px' }}>
        👤 المظهر الخارجي
      </h2>
      
      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">الطول (سم) *</label>
          <input 
            type="number" 
            className="input" 
            value={profile.height} 
            onChange={(e) => setProfile({...profile, height: e.target.value})} 
            placeholder="مثال: 170"
            min="140"
            max="220"
            required 
          />
        </div>
        <div className="form-group">
          <label className="form-label">الوزن (كجم) *</label>
          <input 
            type="number" 
            className="input" 
            value={profile.weight} 
            onChange={(e) => setProfile({...profile, weight: e.target.value})} 
            placeholder="مثال: 70"
            min="40"
            max="200"
            required 
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">لون البشرة *</label>
        <select 
          className="select" 
          value={profile.skin_color} 
          onChange={(e) => setProfile({...profile, skin_color: e.target.value})} 
          required
        >
          <option value="">اختر لون البشرة</option>
          <option value="أبيض ناصع">أبيض ناصع</option>
          <option value="أبيض">أبيض</option>
          <option value="حنطي">حنطي (قمحي)</option>
          <option value="أسمر">أسمر</option>
          <option value="أسود">أسود</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">
          تقييم جمالك الذاتي: <span style={{color: 'var(--primary)', fontSize: '20px', fontWeight: 'bold'}}>{profile.beauty_rating}/10</span>
        </label>
        <input 
          type="range" 
          min="1" 
          max="10" 
          value={profile.beauty_rating} 
          onChange={(e) => setProfile({...profile, beauty_rating: e.target.value})} 
          style={{width: '100%', height: '8px', cursor: 'pointer'}}
        />
        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-light)', marginTop: '4px'}}>
          <span>1</span>
          <span>10</span>
        </div>
      </div>

      {/* أسئلة الشعر - للنساء فقط */}
      {profile.gender === 'female' && (
        <div className="grid grid-2" style={{ marginTop: '16px' }}>
          <div className="form-group">
            <label className="form-label">طول الشعر (اختياري)</label>
            <select 
              className="select"
              data-testid="hair-length-select"
              value={profile.hair_length} 
              onChange={(e) => setProfile({...profile, hair_length: e.target.value})}
            >
              <option value="">اختر طول الشعر</option>
              <option value="طويل">طويل</option>
              <option value="متوسط">متوسط</option>
              <option value="قصير">قصير</option>
              <option value="لاحقاً">لاحقاً</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">لون الشعر (اختياري)</label>
            <select 
              className="select"
              data-testid="hair-color-select"
              value={profile.hair_color} 
              onChange={(e) => setProfile({...profile, hair_color: e.target.value})}
            >
              <option value="">اختر لون الشعر</option>
              <option value="أسود">أسود</option>
              <option value="بني">بني</option>
              <option value="أشقر">أشقر</option>
              <option value="أحمر">أحمر</option>
              <option value="غير ذلك">غير ذلك</option>
              <option value="لاحقاً">لاحقاً</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );

  // Step 4: الالتزام الديني والصحة
  const renderStep4 = () => (
    <div>
      <h2 style={{ marginBottom: '14px', color: 'var(--primary)', fontSize: '24px' }}>
        🕌 الالتزام الديني والصحة
      </h2>
      
      <div className="form-group">
        <label className="form-label">الصلاة *</label>
        <select 
          className="select" 
          value={profile.prayer_level} 
          onChange={(e) => setProfile({...profile, prayer_level: e.target.value})} 
          required
        >
          <option value="">اختر مستوى الصلاة</option>
          <option value="أصلي جميع الفروض">أصلي جميع الفروض 🙏</option>
          <option value="أحياناً">أحياناً</option>
          <option value="لا أصلي">لا أصلي</option>
        </select>
      </div>

      {profile.gender === 'female' && (
        <div className="form-group">
          <label className="form-label">الحجاب *</label>
          <select 
            className="select" 
            value={profile.hijab_status} 
            onChange={(e) => setProfile({...profile, hijab_status: e.target.value})} 
            required
          >
            <option value="">اختر حالة الحجاب</option>
            <option value="منقبة">منقبة (أغطي وجهي) 🧕</option>
            <option value="حجاب">حجاب (أكشف وجهي)</option>
            <option value="لا حجاب">لا أرتدي حجاباً</option>
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">الحالة الصحية *</label>
        <select 
          className="select" 
          value={profile.health_status} 
          onChange={(e) => setProfile({...profile, health_status: e.target.value})} 
          required
        >
          <option value="سليم">سليم صحياً ✓</option>
          <option value="مرض مزمن">لدي مرض مزمن</option>
          <option value="إعاقة">لدي إعاقة</option>
          <option value="إصابة">لدي إصابة</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">التدخين *</label>
        <select 
          className="select" 
          value={profile.smoker || 'لا'} 
          onChange={(e) => setProfile({...profile, smoker: e.target.value})}
          data-testid="smoker-select"
          required
        >
          <option value="لا">لا أدخن 🚭</option>
          <option value="أحياناً">أدخن أحياناً</option>
          <option value="نعم">نعم، مدخن 🚬</option>
          <option value="أقلعت">أقلعت عن التدخين</option>
        </select>
      </div>

      {profile.health_status !== 'سليم' && (
        <div className="form-group">
          <label className="form-label">تفاصيل الحالة الصحية (اختياري)</label>
          <textarea 
            className="textarea" 
            value={profile.health_details} 
            onChange={(e) => setProfile({...profile, health_details: e.target.value})} 
            placeholder="يمكنك توضيح نوع المرض أو الإعاقة إذا أردت"
            rows="3"
          />
        </div>
      )}
    </div>
  );

  // Step 5: الحالة الاجتماعية
  const renderStep5 = () => (
    <div>
      <h2 style={{ marginBottom: '14px', color: 'var(--primary)', fontSize: '24px' }}>
        💑 الحالة الاجتماعية والزواج
      </h2>
      
      <div className="form-group">
        <label className="form-label">الحالة الاجتماعية *</label>
        <select 
          className="select" 
          value={profile.marital_status} 
          onChange={(e) => setProfile({...profile, marital_status: e.target.value})} 
          required
        >
          <option value="">اختر الحالة</option>
          <option value="أعزب">أعزب/عزباء (لم أتزوج من قبل)</option>
          <option value="مطلق">مطلق/مطلقة</option>
          <option value="أرمل">أرمل/أرملة</option>
          <option value="متزوج">متزوج حالياً (معدد)</option>
        </select>
      </div>

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">عدد مرات الزواج السابقة</label>
          <input 
            type="number" 
            className="input" 
            min="0" 
            max="10"
            value={profile.previous_marriages} 
            onChange={(e) => setProfile({...profile, previous_marriages: e.target.value})} 
          />
        </div>
        <div className="form-group">
          <label className="form-label">نوع الزواج المفضل *</label>
          <select 
            className="select" 
            value={profile.marriage_type} 
            onChange={(e) => setProfile({...profile, marriage_type: e.target.value})}
            required
          >
            <option value="عادي">زواج عادي</option>
            <option value="مسيار">زواج مسيار</option>
          </select>
        </div>
      </div>

      {profile.marital_status === 'متزوج' && (
        <div className="form-group">
          <label className="form-label">أبحث عن زوجة *</label>
          <select 
            className="select" 
            value={profile.seeking_wife_number} 
            onChange={(e) => setProfile({...profile, seeking_wife_number: e.target.value})}
            required
          >
            <option value="">اختر</option>
            <option value="ثانية">ثانية</option>
            <option value="ثالثة">ثالثة</option>
            <option value="رابعة">رابعة</option>
          </select>
        </div>
      )}

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">هل تريد إنجاب أطفال؟ *</label>
          <select 
            className="select" 
            value={profile.wants_children} 
            onChange={(e) => setProfile({...profile, wants_children: e.target.value})}
            required
          >
            <option value="نعم">نعم، أريد أطفالاً 👶</option>
            <option value="لا">لا، لا أريد</option>
            <option value="لا يهم">لا يهمني</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">عدد الأطفال الحاليين</label>
          <input 
            type="number" 
            className="input" 
            min="0" 
            max="20"
            value={profile.current_children} 
            onChange={(e) => setProfile({...profile, current_children: e.target.value})} 
          />
        </div>
      </div>

      {profile.current_children > 0 && (
        <div className="form-group">
          <label className="form-label">هل يعيش الأطفال معك؟</label>
          <select 
            className="select" 
            value={profile.children_live_with_user} 
            onChange={(e) => setProfile({...profile, children_live_with_user: e.target.value})}
          >
            <option value="">اختر</option>
            <option value="نعم">نعم، يعيشون معي</option>
            <option value="لا">لا، لا يعيشون معي</option>
            <option value="بعضهم">بعضهم فقط</option>
          </select>
        </div>
      )}
    </div>
  );

  // Step 6: السكن والهوايات
  const renderStep6 = () => (
    <div>
      <h2 style={{ marginBottom: '14px', color: 'var(--primary)', fontSize: '24px' }}>
        🏡 السكن والمعلومات الإضافية
      </h2>
      
      <div className="form-group">
        <label className="form-label">نوع السكن *</label>
        <select 
          className="select" 
          value={profile.housing_type} 
          onChange={(e) => setProfile({...profile, housing_type: e.target.value})} 
          required
        >
          <option value="">اختر نوع السكن</option>
          <option value="ملك">ملك (أملك المنزل)</option>
          <option value="إيجار">إيجار</option>
          <option value="مع العائلة">أسكن مع العائلة</option>
        </select>
      </div>

      {profile.gender === 'male' && (
        <div className="checkbox-container">
          <input 
            type="checkbox" 
            className="checkbox" 
            checked={profile.owns_car} 
            onChange={(e) => setProfile({...profile, owns_car: e.target.checked})} 
            id="owns_car"
          />
          <label htmlFor="owns_car" className="checkbox-label">
            أملك سيارة 🚗
          </label>
        </div>
      )}

      {profile.gender === 'female' && (
        <>
          <div className="checkbox-container">
            <input 
              type="checkbox" 
              className="checkbox" 
              checked={profile.drives_car} 
              onChange={(e) => setProfile({...profile, drives_car: e.target.checked})} 
              id="drives_car"
            />
            <label htmlFor="drives_car" className="checkbox-label">
              أقود سيارة 🚗
            </label>
          </div>
          
          <div className="form-group">
            <label className="form-label">أين تفضلين السكن بعد الزواج؟</label>
            <select 
              className="select" 
              value={profile.housing_preference} 
              onChange={(e) => setProfile({...profile, housing_preference: e.target.value})}
            >
              <option value="">اختاري</option>
              <option value="منزلي الخاص">في منزلي الخاص 🏠</option>
              <option value="منزل الزوج">الانتقال لبيت الزوج</option>
              <option value="لا يهم">لا يهمني (حسب الظروف)</option>
            </select>
          </div>
        </>
      )}

      <div className="form-group">
        <label className="form-label">الهوايات والاهتمامات * (اختر أكثر من هواية)</label>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '8px',
          background: 'var(--light-bg)',
          padding: '16px',
          borderRadius: '12px',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          {[
            'القراءة 📚',
            'السفر ✈️',
            'الرياضة 🏃',
            'الطبخ 🍳',
            'الرسم 🎨',
            'التصوير 📷',
            'المشي 🚶',
            'السباحة 🏊',
            'كرة القدم ⚽',
            'ألعاب الفيديو 🎮',
            'مشاهدة الأفلام 🎬',
            'الموسيقى 🎵',
            'التسوق 🛍️',
            'البستنة 🌱',
            'الخياطة 🧵',
            'التطوع 🤝',
            'تعلم اللغات 🗣️',
            'البرمجة 💻',
            'الكتابة ✍️',
            'الشعر 📝',
            'الخط العربي 🖋️',
            'ركوب الخيل 🐴',
            'الصيد 🎣',
            'التخييم ⛺',
            'اليوغا 🧘',
            'الحرف اليدوية ✂️'
          ].map((hobby) => (
            <label 
              key={hobby} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                background: Array.isArray(profile.hobbies) && profile.hobbies.includes(hobby) ? 'rgba(0, 137, 123, 0.15)' : 'white',
                border: Array.isArray(profile.hobbies) && profile.hobbies.includes(hobby) ? '2px solid var(--primary)' : '1px solid #eee',
                transition: 'all 0.2s'
              }}
            >
              <input 
                type="checkbox" 
                checked={Array.isArray(profile.hobbies) && profile.hobbies.includes(hobby)}
                onChange={(e) => {
                  const currentHobbies = Array.isArray(profile.hobbies) ? profile.hobbies : [];
                  if (e.target.checked) {
                    setProfile({...profile, hobbies: [...currentHobbies, hobby]});
                  } else {
                    setProfile({...profile, hobbies: currentHobbies.filter(h => h !== hobby)});
                  }
                }}
                style={{ display: 'none' }}
              />
              <span style={{ fontSize: '14px' }}>{hobby}</span>
            </label>
          ))}
        </div>
        {Array.isArray(profile.hobbies) && profile.hobbies.length > 0 && (
          <p style={{fontSize: '13px', color: 'var(--primary)', marginTop: '8px'}}>
            ✅ تم اختيار {profile.hobbies.length} هوايات
          </p>
        )}
        <p style={{fontSize: '13px', color: 'var(--text-light)', marginTop: '6px'}}>
          💡 الهوايات المشتركة تزيد من التوافق بينكما
        </p>
      </div>

      {profile.gender === 'female' && (
        <div style={{background: 'var(--light-bg)', padding: '16px', borderRadius: '12px', marginTop: '20px'}}>
          <h4 style={{marginBottom: '16px', color: 'var(--text-dark)'}}>معلومات ولي الأمر (اختياري)</h4>
          
          <div className="form-group">
            <label className="form-label">اسم ولي الأمر</label>
            <input 
              type="text" 
              className="input" 
              value={profile.guardian_name} 
              onChange={(e) => setProfile({...profile, guardian_name: e.target.value})} 
              placeholder="اسم الولي"
            />
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">رقم هاتف الولي</label>
              <input 
                type="tel" 
                className="input" 
                value={profile.guardian_phone} 
                onChange={(e) => setProfile({...profile, guardian_phone: e.target.value})} 
                placeholder="05xxxxxxxx"
              />
            </div>
            <div className="form-group">
              <label className="form-label">صلة القرابة</label>
              <select 
                className="select" 
                value={profile.guardian_relation} 
                onChange={(e) => setProfile({...profile, guardian_relation: e.target.value})}
              >
                <option value="">اختر</option>
                <option value="الأب">الأب</option>
                <option value="الأخ">الأخ</option>
                <option value="العم">العم</option>
                <option value="الجد">الجد</option>
                <option value="آخر">آخر</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Step 7: نبذة عني وشريك/ة أحلامي
  const renderStep7 = () => (
    <div>
      <h2 style={{ marginBottom: '14px', color: 'var(--primary)', fontSize: '24px' }}>
        ✨ عبّر عن نفسك
      </h2>
      
      <div className="form-group">
        <label className="form-label">نبذة عني *</label>
        <textarea 
          className="textarea" 
          value={profile.about_me} 
          onChange={(e) => setProfile({...profile, about_me: e.target.value})} 
          placeholder="اكتب نبذة عن نفسك، شخصيتك، طموحاتك، ما يميزك..."
          rows="5"
          style={{ resize: 'vertical', minHeight: '120px' }}
          required
        />
        <p style={{fontSize: '13px', color: 'var(--text-light)', marginTop: '6px'}}>
          💡 كن صادقاً ومختصراً، هذه فرصتك لتعريف الآخرين بك
        </p>
      </div>

      <div className="form-group" style={{ marginTop: '24px' }}>
        <label className="form-label">
          {profile.gender === 'male' ? 'شريكة أحلامي' : 'شريك أحلامي'} *
        </label>
        <textarea 
          className="textarea" 
          value={profile.ideal_partner} 
          onChange={(e) => setProfile({...profile, ideal_partner: e.target.value})} 
          placeholder={profile.gender === 'male' 
            ? "صِف شريكة حياتك المثالية، ما الصفات التي تبحث عنها؟ ما الأهم بالنسبة لك؟"
            : "صِفي شريك حياتك المثالي، ما الصفات التي تبحثين عنها؟ ما الأهم بالنسبة لك؟"
          }
          rows="5"
          style={{ resize: 'vertical', minHeight: '120px' }}
          required
        />
        <p style={{fontSize: '13px', color: 'var(--text-light)', marginTop: '6px'}}>
          💡 حدد توقعاتك بوضوح لتسهيل إيجاد الشخص المناسب
        </p>
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, rgba(0, 137, 123, 0.1), rgba(0, 180, 162, 0.05))',
        padding: '16px',
        borderRadius: '12px',
        marginTop: '24px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#00897B', fontSize: '14px', margin: 0 }}>
          🎉 أنت على وشك إكمال ملفك الشخصي!
        </p>
      </div>
    </div>
  );

  return (
    <div className="page-bg-aurora" style={{ minHeight: '100vh', padding: '20px 15px' }}>
      {/* مؤثرات بصرية عند إكمال الملف */}
      <SparkleStars trigger={showStars} onComplete={() => setShowStars(false)} />
      <Confetti trigger={showConfetti} onComplete={() => setShowConfetti(false)} />
      
      <div className="card" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '26px', background: 'linear-gradient(135deg, var(--primary), var(--purple))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '8px' }}>
            {isEditMode ? '✏️ تعديل الملف الشخصي' : '💍 إكمال الملف الشخصي'}
          </h1>
          <p style={{ color: 'var(--text-light)', fontSize: '16px' }}>
            الخطوة {step} من 7
          </p>
          <div style={{ background: 'var(--border-color)', height: '10px', borderRadius: '10px', marginTop: '16px', overflow: 'hidden' }}>
            <div style={{ 
              background: 'linear-gradient(135deg, var(--primary), var(--purple))', 
              height: '100%', 
              width: `${(step/7)*100}%`, 
              transition: 'width 0.3s ease',
              boxShadow: '0 2px 10px rgba(255, 107, 157, 0.5)'
            }}></div>
          </div>
        </div>

        {error && <div className="message message-error">{error}</div>}

        <div style={{ minHeight: '400px' }}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          {step === 6 && renderStep6()}
          {step === 7 && renderStep7()}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '2px solid #E5E7EB' }}>
          {step > 1 && (
            <button className="btn btn-outline" onClick={prevStep} style={{ minWidth: '120px' }}>
              ← السابق
            </button>
          )}
          {step < 7 ? (
            <button className="btn btn-primary" style={{flex: 1}} onClick={nextStep}>
              التالي ←
            </button>
          ) : (
            <button 
              className="btn btn-primary" 
              style={{flex: 1}} 
              onClick={handleSubmit} 
              disabled={loading}
            >
              {loading ? '⏳ جاري الحفظ...' : '✅ إنهاء وحفظ الملف الشخصي'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileSetup;
